/**
 * POC 작업 하나를 끝(성공 · 실패)까지 따라가는 폴링.
 *
 * 모양은 `datasets/feature/probePoller.ts` 와 같다. setTimeout 을 잇고, 세대 번호로 늦게 온 응답을 버리고,
 * 문서가 숨겨지면 멈췄다가 돌아오면 곧바로 묻는다. POC 는 분 단위 작업이라 간격은 5초로 고정한다(규칙 §6:
 * RTT 의 수 배).
 *
 * Owns:
 * - 폴링 타이머, 세대 무효화, 문서 가시성 반응, 정리.
 *
 * Does not own:
 * - 조회 — 주입받는 `fetchJob`. 상태 보관 — `mappingEditor.ts`.
 */
import { isPageHidden } from "@/shared/utils/pageVisibility";
import { isPocFinished, type PocJob } from "../domain/mapping";

export const POC_POLL_INTERVAL_MS = 5_000;

export type PocPollerOptions = {
  fetchJob: (jobUuid: string) => Promise<PocJob>;
  onJob: (job: PocJob) => void;
  onError: (error: unknown) => void;
};

export class PocPoller {
  private readonly options: PocPollerOptions;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;
  private jobUuid: string | null = null;
  private disposed = false;
  private visibilityListener: (() => void) | null = null;

  constructor(options: PocPollerOptions) {
    this.options = options;
  }

  /**
   * 작업을 따라가기 시작한다. `immediate` 면 곧바로 묻는다(멈췄다 다시 켤 때). 아니면 한 주기 뒤에 묻는다
   * (접수 응답이 이미 지금 상태를 줬을 때). 같은 작업으로 다시 불러도 조회가 겹치지 않는다.
   */
  start(jobUuid: string, { immediate }: { immediate: boolean }): void {
    if (this.disposed || this.jobUuid === jobUuid) {
      return;
    }
    this.jobUuid = jobUuid;
    this.generation += 1;
    this.clearTimer();
    this.attachVisibility();
    if (immediate) {
      void this.poll(this.generation);
    } else {
      this.schedule(this.generation);
    }
  }

  /** 따라가기를 멈춘다. 진행 중 응답은 세대가 어긋나 버려진다. */
  stop(): void {
    this.jobUuid = null;
    this.generation += 1;
    this.clearTimer();
    this.detachVisibility();
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
  }

  private async poll(generation: number): Promise<void> {
    const jobUuid = this.jobUuid;
    if (jobUuid === null || generation !== this.generation) {
      return;
    }
    try {
      const job = await this.options.fetchJob(jobUuid);
      if (generation !== this.generation) {
        return;
      }
      this.options.onJob(job);
      if (isPocFinished(job.state)) {
        this.stop();
        return;
      }
    } catch (error: unknown) {
      if (generation !== this.generation) {
        return;
      }
      // 일시적 실패로 관측을 끊지 않는다. 다음 주기에 다시 묻는다.
      this.options.onError(error);
    }
    this.schedule(generation);
  }

  private schedule(generation: number): void {
    if (generation !== this.generation) {
      return;
    }
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      if (isPageHidden()) {
        return;
      }
      void this.poll(generation);
    }, POC_POLL_INTERVAL_MS);
  }

  private attachVisibility(): void {
    if (this.visibilityListener !== null || typeof document === "undefined") {
      return;
    }
    const listener = (): void => {
      if (isPageHidden() || this.jobUuid === null) {
        return;
      }
      // 돌아오면 기다리지 않고 지금 상태를 묻는다.
      this.clearTimer();
      void this.poll(this.generation);
    };
    this.visibilityListener = listener;
    document.addEventListener("visibilitychange", listener);
  }

  private detachVisibility(): void {
    if (this.visibilityListener === null || typeof document === "undefined") {
      return;
    }
    document.removeEventListener("visibilitychange", this.visibilityListener);
    this.visibilityListener = null;
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
